/**
 * AdminLayout — shell del panel admin (PT v2-style).
 *
 * Render condicional según role:
 *  - 'superadmin' → ve todas las secciones (Usuarios, Catalogador, Exports, ETL, Settings).
 *  - 'admin'      → ve Exports, ETL, Settings; NO ve Usuarios ni Catalogador.
 *  - 'user'       → solo ve Exports (Jeleni).
 *  - null         → pantalla "sin permisos" con botón volver.
 *
 * Props:
 *  - onExit: callback para volver al matcher principal.
 */
import React, { useState } from 'react';
import { useAuthRole } from './useAuthRole';
import UserManagement from './UserManagement';
import CatalogadorPanel from '../qa/CatalogadorPanel';
import ExportsPanel from './ExportsPanel';
import EtlRunsPanel from './EtlRunsPanel';
import PriceVerificationPanel from './PriceVerificationPanel';
import './AdminLayout.css';

const SECTIONS = [
  {
    id: 'users',
    label: 'Usuarios y Roles',
    group: 'Administración',
    requires: ['superadmin'],
    status: 'ready',
  },
  {
    id: 'catalogador',
    label: 'Catalogador IA',
    group: 'QA',
    requires: ['superadmin'],
    status: 'ready',
  },
  {
    id: 'exports',
    label: 'Exports',
    icon: '📦',
    group: 'Operaciones',
    requires: ['user', 'admin', 'superadmin'],
    status: 'ready',
  },
  {
    id: 'etl',
    label: 'ETL Runs',
    icon: '⚙️',
    group: 'Operaciones',
    requires: ['admin', 'superadmin'],
    status: 'ready',
  },
  {
    id: 'price-verification',
    label: 'Verificación Precios',
    icon: '🔍',
    group: 'Operaciones',
    requires: ['admin', 'superadmin'],
    status: 'ready',
  },
  {
    id: 'settings',
    label: 'Configuración',
    group: 'Operaciones',
    requires: ['admin', 'superadmin'],
    status: 'soon',
  },
];

function Placeholder({ label }) {
  return (
    <div className="admin-placeholder">
      <h3>{label}</h3>
      <p>Próximamente</p>
    </div>
  );
}

export default function AdminLayout({ onExit }) {
  const { email, role, isLoading } = useAuthRole();

  // Default a la primera sección visible según rol.
  const visibleSections = SECTIONS.filter(s => s.requires.includes(role));
  const [activeId, setActiveId] = useState(() => visibleSections[0]?.id || null);

  if (isLoading && !role) {
    return <div className="admin-loading">Cargando permisos...</div>;
  }

  // Permitir entrar si tiene AL MENOS una sección visible.
  // Esto cubre el caso de Jeleni (rol 'user'): puede entrar al admin solo a Exports.
  const hasAnyVisible = SECTIONS.some(s => s.requires.includes(role));
  if (!hasAnyVisible) {
    return (
      <div className="admin-denied">
        <div className="admin-denied-card">
          <h2>Sin permisos para acceder al admin</h2>
          <p>
            Tu cuenta ({email || 'sin email'}) no tiene secciones disponibles.
            Contactá a un superadmin si necesitás acceso.
          </p>
          <button onClick={onExit}>Volver al Matcher</button>
        </div>
      </div>
    );
  }

  const activeSection = visibleSections.find(s => s.id === activeId) || visibleSections[0];

  // Agrupar por "group" para render del sidebar.
  const groups = visibleSections.reduce((acc, s) => {
    if (!acc[s.group]) acc[s.group] = [];
    acc[s.group].push(s);
    return acc;
  }, {});

  const renderSection = () => {
    if (!activeSection) return <Placeholder label="Sin secciones disponibles" />;
    switch (activeSection.id) {
      case 'users':
        return <UserManagement currentRole={role} />;
      case 'catalogador':
        return <CatalogadorPanel />;
      case 'exports':
        return <ExportsPanel />;
      case 'etl':
        return <EtlRunsPanel currentEmail={email} />;
      case 'price-verification':
        return <PriceVerificationPanel />;
      default:
        return <Placeholder label={activeSection.label} />;
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-topbar">
        <div className="admin-topbar-left">
          <h1 className="admin-topbar-title">Admin TIA Matcher</h1>
          <span className="admin-topbar-badge">{role}</span>
        </div>
        <div className="admin-topbar-right">
          <span className="admin-topbar-email">{email}</span>
          <button className="admin-back-btn" onClick={onExit}>
            ← Volver al Matcher
          </button>
        </div>
      </div>

      <div className="admin-body">
        <aside className="admin-sidebar">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <div className="admin-sidebar-group">{group}</div>
              {items.map(item => (
                <div
                  key={item.id}
                  className={`admin-sidebar-item ${activeSection?.id === item.id ? 'active' : ''}`}
                  onClick={() => setActiveId(item.id)}
                >
                  <span>
                    {item.icon && <span style={{ marginRight: 6 }}>{item.icon}</span>}
                    {item.label}
                  </span>
                  {item.status === 'soon' && (
                    <span className="admin-sidebar-item-tag">Próx.</span>
                  )}
                </div>
              ))}
            </div>
          ))}
          <div className="admin-sidebar-spacer" />
          <div className="admin-sidebar-footer">
            TIA Matcher Admin · v0.1
          </div>
        </aside>

        <main className="admin-main">
          <h2 className="admin-section-title">{activeSection?.label || 'Admin'}</h2>
          <p className="admin-section-subtitle">
            {activeSection?.id === 'users' && 'Gestioná usuarios, roles y permisos.'}
            {activeSection?.id === 'catalogador' && 'Prompts, golden sets y QA del Catalogador IA.'}
            {activeSection?.id === 'exports' && 'Generá un XLSX maestro-matches filtrado por toma y sitio.'}
            {activeSection?.id === 'etl' && 'Visibilidad de scripts ETL: runs recientes, health y registro manual.'}
            {activeSection?.id === 'price-verification' && 'Verificación de precios live vs export — muestreo random por sitio (Tipti / Frecuento / Tuti).'}
            {activeSection?.id !== 'users' && activeSection?.id !== 'catalogador'
              && activeSection?.id !== 'exports' && activeSection?.id !== 'etl'
              && activeSection?.id !== 'price-verification'
              && 'Sección en desarrollo.'}
          </p>
          {renderSection()}
        </main>
      </div>
    </div>
  );
}
